<template>
    <modal v-bind:show.sync="show" v-bind:title="trans('Generate with AI')" size="medium">
        <div v-if="noCreditsMessage" class="alert p-3 m-b-4 c-muted br br-c--fade c-accent-bg-light">
            <p class="f-s-2 m-b-0">{{ noCreditsMessage }}</p>
        </div>

        <div class="form-group m-b-4">
            <label class="f-w-bold m-b-2">{{ trans('Select lessons') }}</label>
            <p class="c-muted f-s-2 m-b-0">{{ trans('Choose which lessons in this module to draw questions from.') }}</p>

            <div v-if="selectableLessons.length > 0" class="row row-middle row-collapse p-2">
                <label class="control control-checkbox p-r-2 p-l-3 m-b-0" style="margin-left: 1px">
                    <input
                        type="checkbox"
                        v-bind:checked="allLessonsSelected"
                        v-on:change="toggleSelectAll($event.target.checked)"
                    >
                    <div class="control-indicator control-checkbox-indicator"></div>
                </label>
                <div class="c-muted f-s-2">{{ trans('Select all lessons') }}</div>
            </div>

            <div v-for="lesson in lessons" v-bind:key="lesson.encoded_id" class="row row-middle row-collapse box box-large br p-2 m-b-2">
                <label class="control control-checkbox p-r-2 p-l-3 m-b-0">
                    <input
                        type="checkbox"
                        v-bind:value="lesson.encoded_id"
                        v-model="selectedLessonIds"
                        v-bind:disabled="! lesson.available"
                    >
                    <div class="control-indicator control-checkbox-indicator"></div>
                </label>
                <div class="column">
                    <span v-bind:class="{'c-muted': ! lesson.available}">{{ lesson.name }}</span>
                    <span class="f-s-1 c-muted m-l-2">{{ formatDuration(lesson.duration) }}</span>
                </div>
                <div v-if="! lesson.available" class="m-l-2">
                    <span class="tooltip animate">
                        <span class="i-container i-filled-dark">
                            <!-- /icons/info.svg -->
                            <svg class="icon i-size-tiny inline" width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g class="icon"><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 5a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 12 7zm1.5 10h-3v-1.5h.75v-4h-.75V10h2.25v5.5h.75V17z"/></g></svg>
                        </span>
                        <span class="tooltip-content tooltip-item tooltip--top">{{ lesson.unavailability_reason }}</span>
                    </span>
                </div>
            </div>

            <div v-if="lessons.length === 0" class="c-muted f-s-2">{{ trans('This module has no lessons yet.') }}</div>
            <div v-if="errors.has('lesson_ids')" class="form-help p-t-2">{{ errors.get('lesson_ids') }}</div>
        </div>

        <div class="m-b-2">
            <label class="f-w-bold m-b-2">{{ trans('Number of questions') }}</label>
            <div class="row row-collapse">
                <button
                    v-for="preset in presetCounts" v-bind:key="preset"
                    type="button"
                    class="btn btn-smaller btn-radius--small selector-icon m-r-2"
                    v-bind:class="selectedPreset === preset ? 'btn-primary' : 'btn-outline btn-outline--bright'"
                    v-on:click="selectPreset(preset)"
                >{{ preset }}</button>
                <input
                    type="number"
                    class="btn btn-smaller btn-radius--small btn-outline btn-outline--bright selector-icon m-b-0"
                    v-bind:placeholder="trans('Custom')"
                    v-on:focus="selectPreset('custom')"
                    min="1"
                    style="width: 100px"
                    v-bind:max="maxCount || 1"
                    v-model.number="customCount"
                >
            </div>

            <p class="c-muted f-s-2 m-t-2 m-b-0">{{ spreadHelperText }}</p>
            <div v-if="errors.has('count')" class="form-help p-t-2">{{ errors.get('count') }}</div>
        </div>

        <div slot="footer">
            <div class="row row-collapse">
                <div class="column">
                    <button type="button" class="btn btn-text btn-text--muted-simple m-r-2" v-on:click="show = false">{{ trans('Cancel') }}</button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        v-bind:class="{'btn-loading': loading, 'btn-disabled': ! canSubmit}"
                        v-bind:disabled="loading || ! canSubmit"
                        v-on:click="submit"
                    >
                        {{ trans('Generate') }}
                        <div v-if="loading" class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-bright" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </modal>
</template>
<script>
import Modal from './Modal.vue';
import Errors from '../../errors.js';
import { quizQuestionsGenerateLink } from '../../routes';

const PRESET_COUNTS = [5, 8, 10, 12, 15];
const MAX_QUESTIONS_PER_LESSON = 3;
const MAX_TOTAL_QUESTIONS = 30;

export default {
    components: { Modal },
    props: {
        quiz: {
            type: Object,
            required: true
        },
        lessons: {
            type: Array,
            default: () => []
        },
        hasAiCredits: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            show: false,
            selectedLessonIds: [],
            selectedPreset: null,
            customCount: null,
            loading: false,
            errors: new Errors({})
        };
    },
    computed: {
        presetCounts() {
            return PRESET_COUNTS;
        },
        selectableLessons() {
            return this.lessons.filter((lesson) => lesson.available);
        },
        allLessonsSelected() {
            return this.selectableLessons.length > 0
                && this.selectableLessons.every((lesson) => this.selectedLessonIds.includes(lesson.encoded_id));
        },
        maxCount() {
            return Math.min(this.selectedLessonIds.length * MAX_QUESTIONS_PER_LESSON, MAX_TOTAL_QUESTIONS);
        },
        selectedCount() {
            if (this.selectedPreset === 'custom') {
                return parseInt(this.customCount, 10) || 0;
            }

            return this.selectedPreset || 0;
        },
        spreadHelperText() {
            if (this.selectedLessonIds.length === 0) {
                return this.trans('Select at least one lesson to generate questions.');
            }

            if (this.selectedCount === 0) {
                return this.trans('Choose how many questions to generate.');
            }

            const copy = this.selectedLessonIds.length === 1
                ? this.trans('~:count questions for 1 lesson', {count: this.selectedCount})
                : this.trans('~:count questions for :lessons lessons', {count: this.selectedCount, lessons: this.selectedLessonIds.length});

            if (this.canSubmit) {
                return copy;
            }

            return copy + '. ' + this.trans('The maximum number of questions you can generate is :count.', {count: this.maxCount});
        },
        canSubmit() {
            return this.hasAiCredits
                && this.selectedLessonIds.length > 0
                && this.selectedCount > 0
                && this.selectedCount <= this.maxCount;
        },
        noCreditsMessage() {
            return this.hasAiCredits
                ? null
                : this.trans("You have reached your AI credits limit. Please upgrade your plan to continue using this feature.");
        }
    },
    methods: {
        formatDuration(seconds) {
            const minutes = Math.max(1, Math.round((seconds || 0) / 60));

            return this.trans(':count min', {count: minutes});
        },
        selectPreset(preset) {
            this.selectedPreset = preset;
        },
        toggleSelectAll(checked) {
            this.selectedLessonIds = checked
                ? this.selectableLessons.map((lesson) => lesson.encoded_id)
                : [];
        },
        open() {
            this.selectedLessonIds = [];
            this.selectedPreset = null;
            this.customCount = null;
            this.errors.clear();
            this.show = true;
        },
        submit() {
            if ( ! this.canSubmit || this.loading) {
                return;
            }

            this.loading = true;
            this.errors.clear();

            window.axios
                .post(quizQuestionsGenerateLink(this.quiz), {
                    lesson_ids: this.selectedLessonIds,
                    count: this.selectedCount,
                })
                .then((response) => {
                    this.loading = false;
                    this.show = false;
                    this.$emit('generated', response.data);
                })
                .catch((error) => {
                    this.loading = false;

                    if (error.response && error.response.status === 422) {
                        this.errors.record(error.response.data);

                        return;
                    }

                    this.$toast.danger(
                        (error.response && error.response.data && error.response.data.message)
                            || this.trans('Something went wrong. Please try again.')
                    );
                });
        }
    }
}
</script>
